/**
 * Dois diálogos que aparecem em todo canto assim que a barra lateral ganha
 * menus de contexto: pedir um nome e confirmar algo destrutivo.
 *
 * Ficam aqui em vez de repetidos em cada lugar porque o tratamento de erro é a
 * parte que costuma ser esquecida. Se a chamada ao servidor falha, o diálogo
 * precisa continuar aberto com o texto preservado e a mensagem do erro; fechar
 * e sumir com o que a pessoa digitou é o pior desfecho possível.
 */

import { useEffect, useState } from 'react'
import { ApiError } from '@/lib/api'
import { Button, Field, Input, Modal } from '@/components/ui'

export function NamePrompt({
  open,
  title,
  label,
  initialValue = '',
  placeholder,
  confirmLabel = 'Salvar',
  maxLength,
  hint,
  permitirVazio,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  label: string
  initialValue?: string
  placeholder?: string
  confirmLabel?: string
  maxLength?: number
  hint?: string
  /** Para campos onde apagar tudo é uma escolha, como voltar ao nome original. */
  permitirVazio?: boolean
  onConfirm: (valor: string) => Promise<void>
  onClose: () => void
}) {
  const [valor, setValor] = useState(initialValue)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValor(initialValue)
      setErro(null)
    }
  }, [open, initialValue])

  const confirmar = async () => {
    const limpo = valor.trim()
    if (limpo.length === 0 && !permitirVazio) {
      setErro('Escreva um nome')
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      await onConfirm(limpo)
      onClose()
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível salvar')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} loading={salvando}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Field label={label} required={!permitirVazio} error={erro ?? undefined} hint={hint}>
        <Input
          autoFocus
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void confirmar()}
          maxLength={maxLength}
          placeholder={placeholder}
        />
      </Field>
    </Modal>
  )
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  onConfirm: () => Promise<void>
  onClose: () => void
}) {
  const [executando, setExecutando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) setErro(null)
  }, [open])

  const confirmar = async () => {
    setExecutando(true)
    setErro(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível concluir')
    } finally {
      setExecutando(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={executando}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={() => void confirmar()} loading={executando}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-content-secondary">{description}</p>
      {erro ? <p className="mt-3 text-sm text-danger">{erro}</p> : null}
    </Modal>
  )
}
